/**
 * frontend/src/components/OnboardingModal.ts
 * =============================================
 * Modal de bienvenida y aceptación de Términos y Condiciones.
 *
 * Se muestra a cada usuario hasta que acepta la versión vigente. La
 * aceptación se registra en el servidor (`/api/terminos`), no en el
 * navegador: vale en cualquier equipo y deja constancia de quién, qué versión
 * y cuándo.
 *
 * Incluye:
 *   1. Guía de uso rápida (4 pasos)
 *   2. Términos y Condiciones (jurisdicción Argentina, Ley 25.326)
 *   3. Checkbox de aceptación + botón "Comenzar"
 */

import { ApiError } from "../api";
import { brandMark, icon } from "../utils/icons";
import { prepararDialogo } from "../utils/ui";

/**
 * Versión del texto de abajo. Sube cuando los términos cambian de fondo —el
 * tratamiento de datos, por ejemplo—: cada usuario los vuelve a leer y
 * aceptar, en lugar de darlos por aceptados en silencio.
 */
export const VERSION_TERMINOS = "2";

/**
 * @param aceptar registra la aceptación y sigue. El diálogo se cierra solo si
 *   termina bien: sin constancia en el servidor, el usuario no avanza.
 */
export function showOnboardingModal(aceptar: () => Promise<void>): void {
  // ── Overlay ────────────────────────────────────────────────────────────────
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-labelledby", "onboarding-title");

  overlay.innerHTML = `
    <div class="modal-panel modal-panel--onboarding">

      <div class="modal-header">
        <span class="modal-header__icon">${brandMark()}</span>
        <h2 class="modal-header__title" id="onboarding-title">
          Bienvenido a ConciliA
        </h2>
      </div>

      <div class="modal-body">
        <p class="onboarding-intro">Sus fuentes, un análisis claro y la decisión siempre en sus manos.</p>

        <!-- Guía de uso -->
        <section class="onboarding-steps">
          <h3 class="onboarding-steps__title">¿Cómo funciona?</h3>
          <ol class="onboarding-steps__list">
            <li>
              <span class="step-icon">${icon("upload")}</span>
              <div>
                <strong>Suba los archivos</strong>
                <p>Arrastre o seleccione el extracto bancario y el libro mayor contable, en formato XLSX, XLS o CSV. Ambos deben corresponder al mismo mes y año.</p>
              </div>
            </li>
            <li>
              <span class="step-icon">${icon("spark")}</span>
              <div>
                <strong>ConciliA analiza</strong>
                <p>La IA compara automáticamente cada transacción del extracto contra el libro mayor e identifica diferencias, duplicados y faltantes.</p>
              </div>
            </li>
            <li>
              <span class="step-icon">${icon("eye")}</span>
              <div>
                <strong>Revisión y aprobación</strong>
                <p>El agente presenta un resumen de las diferencias encontradas para que usted las valide antes de continuar. La decisión final es suya.</p>
              </div>
            </li>
            <li>
              <span class="step-icon">${icon("mail")}</span>
              <div>
                <strong>Se genera el reporte</strong>
                <p>Con su aprobación, el agente genera el reporte de conciliación en Excel y lo envía por email a los destinatarios configurados.</p>
              </div>
            </li>
          </ol>
        </section>

        <!-- Términos y Condiciones -->
        <section class="onboarding-tnc">
          <h3 class="onboarding-tnc__title">Términos y Condiciones de Uso</h3>
          <div class="onboarding-tnc__body" id="tnc-body">
            <p><strong>Última actualización:</strong> septiembre 2026</p>

            <h4>1. Partes y aceptación</h4>
            <p>El presente acuerdo regula el uso del software "Agente Conciliador Bancario" (en adelante, "el Software") provisto por Siete Ideas (en adelante, "el Proveedor"). Al utilizar el Software, el Usuario declara haber leído, comprendido y aceptado la totalidad de estos términos.</p>

            <h4>2. Objeto del servicio</h4>
            <p>El Software es una herramienta informática de asistencia para la comparación entre registros del libro mayor contable y extractos bancarios. Su función es identificar diferencias y generar reportes preliminares para revisión humana.</p>

            <h4>3. Naturaleza asistencial — no sustitución profesional</h4>
            <p>El Software es una herramienta de apoyo a tareas contables. <strong>No constituye asesoramiento contable, financiero, impositivo ni legal.</strong> Los reportes generados deben ser revisados y validados por un contador público o profesional habilitado antes de su uso para fines fiscales, legales o financieros. El Usuario asume la responsabilidad exclusiva de las decisiones tomadas a partir de la información procesada.</p>

            <h4>4. Uso aceptable</h4>
            <p>El Usuario se compromete a: (a) utilizar el Software exclusivamente con datos cuya titularidad o autorización de procesamiento posea; (b) no intentar vulnerar la seguridad del sistema ni acceder a datos de terceros sin autorización; (c) no reproducir, distribuir ni sublicenciar el Software sin autorización expresa del Proveedor; (d) no utilizar el Software para procesar datos que sean falsos, fraudulentos o que induzcan a error.</p>

            <h4>5. Privacidad y tratamiento de datos</h4>
            <p>Los archivos que el Usuario carga en el Software se procesan en la memoria del servidor donde se ejecuta la aplicación y se descartan al finalizar cada conciliación: el Software no los guarda en disco. Lo que el Software conserva de cada Usuario son sus perfiles de lectura de archivos, que describen columnas y formatos y no contienen importes ni movimientos. El Proveedor no transmite a terceros los datos financieros procesados, salvo en los casos de envío de reportes que se describen en el punto 6. El tratamiento de datos personales —si existiere— se rige por la <strong>Ley N.° 25.326 de Protección de los Datos Personales</strong> de la República Argentina y sus normas reglamentarias. El Usuario es responsable de garantizar que el procesamiento de datos de terceros cuenta con las autorizaciones correspondientes.</p>

            <h4>6. Envío de reportes por correo electrónico</h4>
            <p>El reporte de conciliación se envía por correo electrónico, desde una casilla del Proveedor, únicamente a los destinatarios que el Usuario indica en sus preferencias. El Usuario es responsable de verificar que esos destinatarios cuentan con autorización para recibir la información financiera contenida en los reportes.</p>
            <p>El servicio de correo del Proveedor conserva una copia de cada mensaje enviado, con sus archivos adjuntos, en la casilla desde la que se envía. El Proveedor no utiliza esas copias con ningún fin distinto del envío, y las elimina a solicitud del Usuario.</p>
            <p>Para brindar soporte, el Proveedor recibe un aviso de cada reporte enviado que indica quién lo generó, cuándo, el período conciliado y el dominio de los destinatarios, sin los reportes ni su contenido. Cuando el Usuario informa un problema desde la aplicación, los archivos que decida adjuntar se envían al equipo de soporte del Proveedor con su autorización expresa, y se utilizan únicamente para resolver ese problema.</p>

            <h4>7. Limitación de responsabilidad</h4>
            <p>En la máxima medida permitida por la legislación argentina aplicable, el Proveedor no será responsable por daños indirectos, pérdida de datos, errores derivados de archivos malformados o de información incorrecta suministrada por el Usuario. La responsabilidad total del Proveedor, en cualquier caso, no excederá el monto abonado por el Usuario en los últimos seis (6) meses calendario.</p>

            <h4>8. Propiedad intelectual</h4>
            <p>El Software, su código fuente, algoritmos, marcas y documentación son propiedad exclusiva del Proveedor y están protegidos por las leyes de propiedad intelectual de la República Argentina (<strong>Ley N.° 11.723</strong> y normas concordantes). Queda prohibida su reproducción total o parcial sin autorización expresa.</p>

            <h4>9. Modificaciones</h4>
            <p>El Proveedor podrá modificar los presentes términos con un preaviso mínimo de 30 días corridos comunicado en la interfaz de la aplicación. El uso continuado del Software luego de vencido dicho plazo implicará la aceptación de los nuevos términos.</p>

            <h4>10. Jurisdicción y ley aplicable</h4>
            <p>El presente acuerdo se rige exclusivamente por las leyes de la <strong>República Argentina</strong>. Cualquier controversia que se suscite en relación con el mismo se someterá a la jurisdicción de los <strong>Tribunales Ordinarios de la Ciudad Autónoma de Buenos Aires</strong>, con renuncia expresa a cualquier otro fuero que pudiera corresponder por razón del domicilio de las partes o por cualquier otra causa.</p>
          </div>
        </section>

      </div>

      <div class="modal-footer modal-footer--onboarding">
        <!-- La aceptación permanece a la vista mientras se leen los términos. -->
        <div class="onboarding-accept">
          <label class="onboarding-accept__label">
            <input type="checkbox" id="tnc-checkbox" class="onboarding-accept__checkbox" />
            He leído y acepto los Términos y Condiciones de uso del Agente Conciliador Bancario.
          </label>
          <p class="onboarding-accept__error" id="tnc-error" role="alert" hidden></p>
        </div>

        <button
          class="btn btn--primary btn--large"
          id="btn-comenzar"
          type="button"
          disabled
        >
          Comenzar
        </button>
      </div>

    </div>
  `;

  document.body.appendChild(overlay);
  prepararDialogo(overlay);

  // ── Lógica de aceptación ──────────────────────────────────────────────────
  const checkbox = overlay.querySelector<HTMLInputElement>("#tnc-checkbox")!;
  const btnComenzar = overlay.querySelector<HTMLButtonElement>("#btn-comenzar")!;

  checkbox.addEventListener("change", () => {
    btnComenzar.disabled = !checkbox.checked;
  });

  const error = overlay.querySelector<HTMLElement>("#tnc-error")!;

  btnComenzar.addEventListener("click", async () => {
    if (!checkbox.checked) return;
    btnComenzar.disabled = true;
    error.hidden = true;
    try {
      await aceptar();
      overlay.remove();
    } catch (err) {
      error.textContent = err instanceof ApiError
        ? err.message
        : "No se pudo registrar la aceptación. Intente de nuevo.";
      error.hidden = false;
      btnComenzar.disabled = !checkbox.checked;
    }
  });
}
